<template>
  <v-container fluid>
    <!-- Header avec actions rapides -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <div>
            <h1 class="text-h4 mb-2">Dashboard RH</h1>
            <p class="text-subtitle-1 text-grey">
              Bienvenue {{ userStore.user?.name }}. Voici un aperçu de vos activités.
            </p>
          </div>
          <div class="d-flex gap-2">
            <v-btn color="primary" prepend-icon="mdi-plus" @click="$router.push('/hr/needs')">
              Nouveau besoin
            </v-btn>
            <v-btn color="secondary" prepend-icon="mdi-briefcase-plus" @click="$router.push('/hr/jobs')">
              Nouveau poste
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Statistiques principales -->
    <v-row class="mb-6">
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-2 text-grey mb-1">Candidatures</p>
                <h2 class="text-h4">{{ stats.totalApplications }}</h2>
                <v-chip size="small" :color="getChangeColor(stats.applicationsChange)" class="mt-2">
                  {{ stats.applicationsChange }}%
                </v-chip>
              </div>
              <v-avatar color="primary" size="48">
                <v-icon size="24">mdi-account-multiple</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-2 text-grey mb-1">Postes actifs</p>
                <h2 class="text-h4">{{ stats.activeJobs }}</h2>
                <v-chip size="small" :color="getChangeColor(stats.jobsChange)" class="mt-2">
                  {{ stats.jobsChange }}%
                </v-chip>
              </div>
              <v-avatar color="success" size="48">
                <v-icon size="24">mdi-briefcase</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-2 text-grey mb-1">Entretiens</p>
                <h2 class="text-h4">{{ stats.upcomingInterviews }}</h2>
                <p class="text-body-2 text-grey mt-1">Cette semaine</p>
              </div>
              <v-avatar color="warning" size="48">
                <v-icon size="24">mdi-calendar-clock</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-2 text-grey mb-1">Taux de conversion</p>
                <h2 class="text-h4">{{ stats.conversionRate }}%</h2>
                <p class="text-body-2 text-grey mt-1">Ce mois</p>
              </div>
              <v-avatar color="info" size="48">
                <v-icon size="24">mdi-trending-up</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Graphiques et analyses -->
    <v-row class="mb-6">
      <v-col cols="12" lg="8">
        <v-card>
          <v-card-title>
            <span class="text-h6">Évolution des candidatures</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="chartPeriod" variant="outlined" density="compact">
              <v-btn value="week">7j</v-btn>
              <v-btn value="month">30j</v-btn>
              <v-btn value="quarter">3m</v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <CandidatesByJobChart :period="chartPeriod" />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card class="mb-4">
          <v-card-title>Statut des candidatures</v-card-title>
          <v-card-text>
            <CandidateStatusChart />
          </v-card-text>
        </v-card>

        <v-card>
          <v-card-title>Répartition par expérience</v-card-title>
          <v-card-text>
            <ExperienceChart />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Activités récentes et actions -->
    <v-row>
      <v-col cols="12" lg="6">
        <v-card>
          <v-card-title>
            <span class="text-h6">Candidatures récentes</span>
            <v-spacer></v-spacer>
            <v-btn variant="text" size="small" @click="$router.push('/hr/candidates')">
              Voir tout
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-list v-if="recentApplications.length > 0">
              <v-list-item v-for="application in recentApplications" :key="application.id"
                @click="viewCandidate(application)" class="cursor-pointer">
                <template v-slot:prepend>
                  <v-avatar>
                    <v-img v-if="application.avatar" :src="application.avatar" :alt="application.name"></v-img>
                    <v-icon v-else>mdi-account</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title>{{ application.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ application.jobTitle }} • {{ formatTimeAgo(application.appliedAt) }}
                </v-list-item-subtitle>

                <template v-slot:append>
                  <v-chip size="small" :color="getStatusColor(application.status)">
                    {{ getStatusText(application.status) }}
                  </v-chip>
                </template>
              </v-list-item>
            </v-list>

            <div v-else class="text-center py-8">
              <v-icon size="48" color="grey">mdi-inbox</v-icon>
              <p class="text-body-1 mt-2">Aucune candidature récente</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card>
          <v-card-title>
            <span class="text-h6">Entretiens à venir</span>
            <v-spacer></v-spacer>
            <v-btn variant="text" size="small" @click="openCalendar">
              Calendrier
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-list v-if="upcomingInterviews.length > 0">
              <v-list-item v-for="interview in upcomingInterviews" :key="interview.id" @click="viewInterview(interview)"
                class="cursor-pointer">
                <template v-slot:prepend>
                  <v-avatar color="primary">
                    <v-icon>mdi-calendar</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title>{{ interview.candidateName }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ interview.jobTitle }}<br>
                  {{ formatDate(interview.scheduledAt) }} à {{ formatTime(interview.scheduledAt) }}
                </v-list-item-subtitle>

                <template v-slot:append>
                  <v-icon color="primary">mdi-chevron-right</v-icon>
                </template>
              </v-list-item>
            </v-list>

            <div v-else class="text-center py-8">
              <v-icon size="48" color="grey">mdi-calendar-blank</v-icon>
              <p class="text-body-1 mt-2">Aucun entretien programmé</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Actions rapides -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card>
          <v-card-title>Actions rapides</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-btn block variant="outlined" prepend-icon="mdi-file-export" @click="exportCandidates"
                  :loading="isExporting.candidates">
                  Exporter candidats
                </v-btn>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn block variant="outlined" prepend-icon="mdi-email-multiple" @click="sendBulkEmails">
                  Emails groupés
                </v-btn>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn block variant="outlined" prepend-icon="mdi-chart-line" @click="generateReport"
                  :loading="isExporting.report">
                  Rapport mensuel
                </v-btn>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn block variant="outlined" prepend-icon="mdi-cog" @click="$router.push('/admin/settings')">
                  Paramètres
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import CandidatesByJobChart from '@/components/charts/CandidatesByJobChart.vue'
import CandidateStatusChart from '@/components/charts/CandidateStatusChart.vue'
import ExperienceChart from '@/components/charts/ExperienceChart.vue'
import { useHRStore } from '@/stores/hr'
import { useSnackbarStore } from '@/stores/snackbar'
import { useUserStore } from '@/stores/user'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale/fr'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import apiService from '@/services/api'

const router = useRouter()
const userStore = useUserStore()
const hrStore = useHRStore()
const snackbarStore = useSnackbarStore()

const chartPeriod = ref('month')
const isExporting = ref({
  candidates: false,
  report: false
})

const stats = ref({
  totalApplications: 0,
  applicationsChange: 0,
  activeJobs: 0,
  jobsChange: 0,
  upcomingInterviews: 0,
  conversionRate: 0
})

const recentApplications = ref([])
const upcomingInterviews = ref([])

const getChangeColor = (change) => {
  return change >= 0 ? 'success' : 'error'
}

const getStatusColor = (status) => {
  const colors = {
    'applied': 'blue',
    'reviewing': 'orange',
    'interview_scheduled': 'purple',
    'interview_completed': 'indigo',
    'accepted': 'green',
    'rejected': 'red',
    'on_hold': 'grey'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status) => {
  const texts = {
    'applied': 'Candidature reçue',
    'reviewing': 'En cours d\'examen',
    'interview_scheduled': 'Entretien programmé',
    'interview_completed': 'Entretien terminé',
    'accepted': 'Acceptée',
    'rejected': 'Refusée',
    'on_hold': 'En attente'
  }
  return texts[status] || status
}

const formatTimeAgo = (date) => {
  const now = new Date()
  const past = new Date(date)
  const diffInHours = Math.floor((now - past) / (1000 * 60 * 60))

  if (diffInHours < 1) return 'Il y a moins d\'1h'
  if (diffInHours < 24) return `Il y a ${diffInHours}h`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `Il y a ${diffInDays}j`

  return format(past, 'dd MMM', { locale: fr })
}

const formatDate = (date) => {
  return format(new Date(date), 'dd MMMM yyyy', { locale: fr })
}

const formatTime = (date) => {
  return format(new Date(date), 'HH:mm', { locale: fr })
}

const loadDashboardData = async () => {
  try {
    const { data } = await apiService.hr.getAnalytics()
    // data.stats, data.recentApplications, data.upcomingInterviews
    stats.value = data.stats
    recentApplications.value = data.recentApplications || []
    upcomingInterviews.value = data.upcomingInterviews || []
  } catch (error) {
    console.error('Erreur lors du chargement du dashboard:', error)
    snackbarStore.error('Erreur lors du chargement des données')
  }
}

const viewCandidate = (application) => {
  router.push({
    name: 'hr-candidates',
    query: { candidateId: application.candidateId }
  })
}

const viewInterview = (interview) => {
  router.push({
    name: 'hr-candidates',
    query: { candidateId: interview.candidateId, tab: 'interviews' }
  })
}

const openCalendar = () => {
  window.open('https://calendar.google.com', '_blank')
}

const exportCandidates = async () => {
  try {
    isExporting.value.candidates = true
    const blob = await hrStore.exportCandidates({})

    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `candidats_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
    link.click()
    window.URL.revokeObjectURL(url)

    snackbarStore.success('Export des candidats terminé')
  } catch (error) {
    console.error('Erreur lors de l\'export:', error)
    snackbarStore.error('Erreur lors de l\'export des candidats')
  } finally {
    isExporting.value.candidates = false
  }
}

const sendBulkEmails = () => {
  router.push({
    name: 'hr-candidates',
    query: { action: 'bulk-email' }
  })
}

const generateReport = async () => {
  try {
    isExporting.value.report = true
    const currentDate = new Date()
    const reportName = `rapport_mensuel_${format(currentDate, 'yyyy-MM')}.pdf`

    // Simuler la génération d'un rapport
    await new Promise(resolve => setTimeout(resolve, 2000))

    snackbarStore.success(`Rapport mensuel généré: ${reportName}`)
  } catch (error) {
    console.error('Erreur lors de la génération du rapport:', error)
    snackbarStore.error('Erreur lors de la génération du rapport')
  } finally {
    isExporting.value.report = false
  }
}

onMounted(() => {
  loadDashboardData()
})
</script>

<style scoped>
.v-card {
  transition: elevation 0.3s ease;
}

.v-card:hover {
  elevation: 4;
}

.cursor-pointer {
  cursor: pointer;
}

.gap-2 {
  gap: 8px;
}
</style>
